// Platform display names mapping
export const PLATFORM_DISPLAY_NAMES: Record<string, string> = {
  openai: "ChatGPT",
  chatgpt: "ChatGPT", // Legacy compatibility
  claude: "Claude", 
  gemini: "Google Gemini",
  perplexity: "Perplexity",
  google_ai_overview: "Google AI Overview"
};

// Platform logo paths mapping
export const PLATFORM_LOGOS: Record<string, string> = {
  openai: "/images/chatgpt-logo.svg",
  chatgpt: "/images/chatgpt-logo.svg", // Legacy compatibility
  claude: "/images/claude-logo.svg",
  gemini: "/images/google-gemini-logo.svg",
  perplexity: "/images/perplexity-logo.svg",
  google_ai_overview: "/images/google-logo.svg",
  "google-gemini": "/images/google-gemini-logo.svg", // Legacy key used by report UI
  "microsoft-copilot": "/images/microsoft-copilot-logo.svg",
};

// Platform order mapping based on LLM_PLATFORM_CONFIGS
// Lower numbers appear first (higher priority)
export const PLATFORM_ORDER: Record<string, number> = {
  google_ai_overview: 0, // Always show first
  openai: 1, // ChatGPT
  gemini: 2, // Google Gemini
  perplexity: 3, // Perplexity
  claude: 4, // Claude
  chatgpt: 1, // Legacy compatibility - same as openai
};

/**
 * Get the user-friendly display name for a platform
 */
export function getPlatformDisplayName(platform: string): string {
  return PLATFORM_DISPLAY_NAMES[platform] || platform.charAt(0).toUpperCase() + platform.slice(1);
}

/**
 * Get the logo path for a platform
 */
export function getPlatformLogo(platform: string): string | null {
  return PLATFORM_LOGOS[platform] || null;
}

/**
 * Get the logo path for a platform in "grounding search" context.
 * For Gemini we show the Google logo (same index used for grounding), not the Gemini product logo.
 */
export function getGroundingPlatformLogo(platform: string): string | null {
  if (platform === "gemini" || platform === "google_ai_overview") {
    return "/images/google-logo.svg";
  }
  return getPlatformLogo(platform);
}

/**
 * Get the display name for a platform in "grounding search" context.
 * Gemini and Google AI Overview both use Google's index, so we show "Google" for both
 * so users focus on optimizing for AI Overviews.
 */
export function getGroundingPlatformDisplayName(platform: string): string {
  if (platform === "gemini" || platform === "google_ai_overview") {
    return "Google";
  }
  return getPlatformDisplayName(platform);
}

/**
 * Get the order priority for a platform (lower numbers = higher priority)
 */
export function getPlatformOrder(platform: string): number {
  return PLATFORM_ORDER[platform] ?? 999; // Default to low priority for unknown platforms
}

/**
 * Sort platforms by their order priority
 * Google AI Overview appears first, followed by platforms in order of user base/importance
 */
export function sortPlatformsByOrder<T extends { platform: string }>(platforms: T[]): T[] {
  return [...platforms].sort((a, b) => {
    const orderA = getPlatformOrder(a.platform);
    const orderB = getPlatformOrder(b.platform);
    return orderA - orderB;
  });
}

/**
 * Sort platform keys by their order priority
 */
export function sortPlatformKeysByOrder(platformKeys: string[]): string[] {
  return [...platformKeys].sort((a, b) => {
    const orderA = getPlatformOrder(a);
    const orderB = getPlatformOrder(b);
    return orderA - orderB;
  });
}

